<script setup>
import {computed} from 'vue'
import PageContentArea from "./PageContentArea.vue"
import Button from '@/common/Button.vue'
import PageContentSectionsTemplateMenu from "./PageContentSectionsTemplateMenu.vue"
import PageContentSectionsContainerMenu from "./PageContentSectionsContainerMenu.vue"
import api from '@/services/api'
import {usePageSectionsStore} from '@/stores/pageSections'
import {usePageContentDataStore} from '@/stores/pageContentData'
import {useModalsStore} from '@/stores/modals'

const props = defineProps({
    pageId: Number
})

const pageSectionsStore = usePageSectionsStore()
const pageContentDataStore = usePageContentDataStore()
const modalsStore = useModalsStore()

const pageSections = computed(() => {
    return (pageSectionsStore.items || []).filter(o => o.pageId === props.pageId)
})

const addSection = async () => {
    await api.put(`api/page-section/${props.pageId}`)
    await pageSectionsStore.init(true)
}

const moveSectionUp = async (i) => {
    if (i === 0) {
        console.log('The first section cannot be moved higher.')
        return
    }

    const section = pageSections.value[i]
    try {
        await api.patch(`api/page-section/${section.id}/up`)
        await pageSectionsStore.init(true)
    } catch (err) {
        // Handle error if needed
    }
}

const moveSectionDown = async (i) => {
    if (i === pageSections.value.length - 1) {
        console.log('The last section cannot be moved lower.')
        return
    }

    const section = pageSections.value[i]
    try {
        await api.patch(`api/page-section/${section.id}/down`)
        await pageSectionsStore.init(true)
    } catch (err) {
        // Handle error if needed
    }
}

const deleteSection = (i) => {
    const section = pageSections.value[i]

    modalsStore.show({
        componentName: 'ConfirmModal',
        obj: {
            modalTitle: 'Delete section',
            modalBody: `
                <p class="alert alert-danger">This <strong>cannot</strong> be undone.</p>
                <p class="mb-0">Are you sure?</p>
            `,
            onConfirm: async () => {
                try {
                    await api.delete(`api/page-section/${section.id}`)
                    await pageSectionsStore.init(true)

                    // Removes any propagated content that was contained within the deleted section
                    // from the content's Page Use control.
                    await pageContentDataStore.init()
                } catch (err) {
                    // Handle error if needed
                }
            }
        }
    })
}

const showAddContentModal = (pageSectionLinkId, contentArea) => {
    modalsStore.show({
        componentName: 'AddContentModal',
        obj: {
            onSelect: async (o) => {
                console.log({pageId: props.pageId, contentId: o.contentId, contentArea})
                try {
                    await api.post('api/save-content-to-page', {
                        pageId: props.pageId,
                        pageSectionLinkId,
                        contentId: o.contentId,
                        contentArea
                    })

                    await pageContentDataStore.init()
                } catch (err) {
                    // Handle error if needed
                }
            }
        }
    })
}
</script>

<template>
    <div>
        <div v-for="(section, i) in pageSections" :key="section.id" class="card mb-3">
            <div class="card-header py-1 pe-2 d-flex align-items-center">
                <span class="flex-fill">
                    Section {{ i + 1 }}
                    <code class="text-secondary"> <small>#{{ section.id }}</small></code>
                </span>

                <div class="dropdown d-inline-block">
                    <Button class="btn-sm" data-bs-toggle="dropdown">
                        <i class="bi bi-three-dots"/>
                    </Button>
                    <div class="dropdown-menu">
                        <div class="dropdown-item">
                            <PageContentSectionsTemplateMenu :section-id="section.id"/>
                        </div>
                        <div class="dropdown-item">
                            <PageContentSectionsContainerMenu :section-id="section.id"/>
                        </div>
                    </div>
                </div>
                <Button @click="moveSectionUp(i)" class="btn-sm"><i class="bi bi-chevron-up"/></Button>
                <Button @click="moveSectionDown(i)" class="btn-sm"><i class="bi bi-chevron-down"/></Button>
                <Button @click="deleteSection(i)" class="btn-sm"><i class="bi bi-x-lg"/></Button>
            </div>

            <div class="card-body">
                <div v-for="(area, k) in section.areas" :key="area.value" class="card"
                     :class="{'mb-3': k < section.areas.length - 1}">
                    <div class="card-header py-1 pe-2 d-flex align-items-center">
                        <span class="flex-fill">
                            {{ area.text }}
                            <code class="text-secondary"> <small>{{ area.value }}</small></code>
                        </span>

                        <Button @click="showAddContentModal(section.id, area.value)" class="btn-sm">
                            <i class="bi bi-plus-lg"/>
                        </Button>
                    </div>
                    <PageContentArea :page-section-link-id="section.id" :area="area.value"/>
                </div>
            </div>
        </div>

        <Button @click="addSection"><i class="bi bi-plus-lg"/> Add section</Button>
    </div>
</template>